import RBush from 'rbush';
import type { Stroke, BBox } from '@blackboard/shared';

interface Node {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  stroke: Stroke;
}

export class StrokeIndex {
  private tree = new RBush<Node>();
  private byId = new Map<number, Node>();

  insert(s: Stroke) {
    if (this.byId.has(s.id)) return;
    const node: Node = {
      minX: s.bbox.x1,
      minY: s.bbox.y1,
      maxX: s.bbox.x2,
      maxY: s.bbox.y2,
      stroke: s,
    };
    this.tree.insert(node);
    this.byId.set(s.id, node);
  }

  bulkLoad(strokes: Stroke[]) {
    const nodes: Node[] = [];
    for (const s of strokes) {
      if (this.byId.has(s.id)) continue;
      const n: Node = {
        minX: s.bbox.x1,
        minY: s.bbox.y1,
        maxX: s.bbox.x2,
        maxY: s.bbox.y2,
        stroke: s,
      };
      nodes.push(n);
      this.byId.set(s.id, n);
    }
    if (nodes.length > 0) this.tree.load(nodes);
  }

  remove(id: number) {
    const n = this.byId.get(id);
    if (!n) return;
    this.tree.remove(n);
    this.byId.delete(id);
  }

  search(bbox: BBox): Stroke[] {
    return this.tree
      .search({ minX: bbox.x1, minY: bbox.y1, maxX: bbox.x2, maxY: bbox.y2 })
      .map((n) => n.stroke);
  }

  getById(id: number) {
    return this.byId.get(id)?.stroke ?? null;
  }

  size() {
    return this.byId.size;
  }

  clear() {
    this.tree.clear();
    this.byId.clear();
  }

  hitTest(worldX: number, worldY: number, tolerance = 8): Stroke[] {
    return this.search({
      x1: worldX - tolerance,
      y1: worldY - tolerance,
      x2: worldX + tolerance,
      y2: worldY + tolerance,
    });
  }

  /**
   * 精确拾取指针下的单条笔画（橡皮/悬停用）。
   *
   * 与 bbox hitTest 的区别：bbox 会把「折线Bounding大但墨迹不在点上」的
   * 笔画一并命中——对角线笔画的 bbox 覆盖大片空白，点空白处也命中，一次
   * 全擦就是大面积误伤。这里对每个候选算点到折线的真实几何距离，减去
   * 笔画显示半径（点到墨迹边缘的距离）后仍 ≤ tol 才算点中；多条重叠时
   * 取 id 最大（最新 = 视觉最上层）的那条 —— 擦一下只擦一条，想全擦多
   * 擦几下即可。
   */
  pickStrokeAt(
    worldX: number,
    worldY: number,
    tolWorld: number,
    radiusOf: (s: Stroke) => number,
  ): Stroke | null {
    // 粗筛：bbox 扩一个最大可能的笔画半径 + 容差
    const MAX_R = 64;
    const cand = this.search({
      x1: worldX - tolWorld - MAX_R,
      y1: worldY - tolWorld - MAX_R,
      x2: worldX + tolWorld + MAX_R,
      y2: worldY + tolWorld + MAX_R,
    });
    let best: Stroke | null = null;
    let bestPenalty = Infinity;
    for (const s of cand) {
      const d = distToPolyline(worldX, worldY, s.points);
      const penalty = d - radiusOf(s);
      if (penalty > tolWorld) continue;
      // penalty 更小 = 更实点在墨上；同 penalty 时 id 大 = 视觉更上层
      if (penalty < bestPenalty - 0.001 || (Math.abs(penalty - bestPenalty) <= 0.001 && best && s.id > best.id)) {
        best = s;
        bestPenalty = penalty;
      }
    }
    return best;
  }
}

/** 点到折线的最小距离；长笔画按最多 ~256 段采样，精度足够（间距远小于笔宽）。 */
function distToPolyline(x: number, y: number, pts: { x: number; y: number }[]): number {
  if (pts.length === 0) return Infinity;
  const step = Math.max(1, Math.floor(pts.length / 256));
  let min = Infinity;
  let px = pts[0]!.x, py = pts[0]!.y;
  min = Math.min(min, dist2sqrt(x, y, px, py));
  for (let i = step; i < pts.length; i += step) {
    const q = pts[i]!;
    min = Math.min(min, segDist(x, y, px, py, q.x, q.y));
    px = q.x; py = q.y;
  }
  const last = pts[pts.length - 1]!;
  min = Math.min(min, dist2sqrt(x, y, last.x, last.y));
  return min;
}

function dist2sqrt(x: number, y: number, px: number, py: number): number {
  return Math.hypot(x - px, y - py);
}

function segDist(x: number, y: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(x - ax, y - ay);
  let t = ((x - ax) * dx + (y - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(x - (ax + t * dx), y - (ay + t * dy));
}
